import { useEffect, useState } from "react";
import { api, formatApiError, ROLE_LABELS } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const ROLES = ["company_admin", "staff"];
const EMPTY_FORM = { login_id: "", email: "", name: "", password: "", role: "staff", phone: "" };

export default function Users() {
    const { user: me } = useAuth();
    const [list, setList] = useState([]);
    const [open, setOpen] = useState(false);
    const [editId, setEditId] = useState(null); // null when creating
    const [form, setForm] = useState(EMPTY_FORM);

    const load = async () => {
        const { data } = await api.get("/users");
        setList(data);
    };
    useEffect(() => { load(); }, []);

    const openCreate = () => {
        setEditId(null);
        setForm(EMPTY_FORM);
        setOpen(true);
    };

    const openEdit = (u) => {
        setEditId(u.id);
        setForm({
            login_id: u.login_id || "",
            email: u.email || "",
            name: u.name || "",
            password: "", // blank == keep current
            role: u.role,
            phone: u.phone || "",
        });
        setOpen(true);
    };

    const submit = async (e) => {
        e.preventDefault();
        try {
            if (editId) {
                // PATCH only fields that the admin actually wants to change.
                // Empty string for login_id/email/phone explicitly clears them.
                const payload = {
                    name: form.name,
                    login_id: form.login_id,
                    email: form.email,
                    phone: form.phone,
                    role: form.role,
                };
                if (form.password) payload.password = form.password;
                await api.patch(`/users/${editId}`, payload);
                toast.success("ユーザーを更新しました");
            } else {
                const payload = { ...form };
                if (!payload.email) delete payload.email;
                if (!payload.login_id) delete payload.login_id;
                if (!payload.phone) delete payload.phone;
                await api.post("/users", payload);
                toast.success("ユーザーを作成しました");
            }
            setOpen(false);
            setEditId(null);
            setForm(EMPTY_FORM);
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const updateRole = async (id, role) => {
        try {
            await api.patch(`/users/${id}/role`, { role });
            toast.success("権限を更新しました");
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const remove = async (u) => {
        if (!window.confirm(`${u.name} を削除しますか？\nこの操作は取り消せません。`)) return;
        try {
            await api.delete(`/users/${u.id}`);
            toast.success("削除しました");
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    return (
        <div className="space-y-5" data-testid="users-page">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <p className="prc-label-overline">ユーザー・権限</p>
                    <h1 className="text-[1.685rem] font-bold tracking-tight">RBAC ユーザー管理</h1>
                </div>
                <button onClick={openCreate} className="prc-btn-primary inline-flex items-center gap-2" data-testid="user-new-btn">
                    <Plus size={16} />ユーザー追加
                </button>
            </div>

            <div className="prc-card overflow-x-auto">
                <table className="prc-table" data-testid="users-table">
                    <thead>
                        <tr><th>氏名</th><th>ログインID</th><th>メール</th><th>電話</th><th>権限</th><th></th></tr>
                    </thead>
                    <tbody>
                        {list.map((u) => (
                            <tr key={u.id} data-testid={`user-row-${u.id}`}>
                                <td>{u.name}</td>
                                <td className="font-mono text-xs">{u.login_id || "-"}</td>
                                <td className="font-mono text-xs">{u.email || "-"}</td>
                                <td>{u.phone || "-"}</td>
                                <td>
                                    {u.id === me?.id ? (
                                        <span className="prc-badge muted">{ROLE_LABELS[u.role]}</span>
                                    ) : (
                                        <select
                                            value={u.role}
                                            onChange={(e) => updateRole(u.id, e.target.value)}
                                            className="prc-input py-1 text-xs"
                                            data-testid={`user-role-${u.id}`}
                                        >
                                            {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                                        </select>
                                    )}
                                </td>
                                <td className="text-right whitespace-nowrap">
                                    <button
                                        onClick={() => openEdit(u)}
                                        className="text-xs text-[var(--prc-green)] inline-flex items-center gap-1 mr-3 hover:underline"
                                        data-testid={`user-edit-${u.id}`}
                                    >
                                        <PencilSimple size={16} />編集
                                    </button>
                                    {u.id !== me?.id && (
                                        <button onClick={() => remove(u)} className="text-xs text-[var(--prc-red)] inline-flex items-center gap-1 hover:underline" data-testid={`user-del-${u.id}`}>
                                            <Trash size={16} />削除
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {open && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="user-dialog">
                    <form onSubmit={submit} className="prc-card p-6 max-w-md w-full">
                        <p className="prc-label-overline mb-1">{editId ? "ユーザー編集" : "ユーザー追加"}</p>
                        <h2 className="text-xl font-bold mb-1">{editId ? "ユーザー情報を更新" : "新規登録"}</h2>
                        <p className="text-xs text-[var(--prc-muted)] mb-4">
                            ログインID または メールアドレスのいずれかを設定してください（両方可）
                        </p>
                        <div className="space-y-3">
                            <Input label="氏名 *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="user-name" />
                            <Input
                                label="ログインID（推奨）"
                                value={form.login_id}
                                onChange={(v) => setForm({ ...form, login_id: v.toLowerCase().replace(/[^a-z0-9_-]/g, "") })}
                                testid="user-login-id"
                                placeholder="例: tanaka_taro"
                                helper="英小文字・数字・アンダースコア・ハイフンで 3〜32 文字"
                            />
                            <Input label="メールアドレス（任意）" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="user-email" />
                            <Input label="電話番号" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="user-phone" />
                            <Input
                                label={editId ? "新しいパスワード（変更する場合のみ）" : "初期パスワード *"}
                                type="password"
                                value={form.password}
                                onChange={(v) => setForm({ ...form, password: v })}
                                required={!editId}
                                testid="user-password"
                                helper={editId ? "空欄なら現在のパスワードを保持します" : null}
                            />
                            <div>
                                <label className="prc-label-overline block mb-1">権限</label>
                                <select className="prc-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-role-select">
                                    {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-5">
                            <button type="button" onClick={() => { setOpen(false); setEditId(null); }} className="prc-btn-ghost" data-testid="user-cancel">キャンセル</button>
                            <button type="submit" className="prc-btn-primary" data-testid="user-submit">{editId ? "更新" : "登録"}</button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}

function Input({ label, value, onChange, type = "text", required, testid, placeholder, helper }) {
    return (
        <div>
            <label className="prc-label-overline block mb-1">{label}</label>
            <input
                type={type}
                required={required}
                className="prc-input"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder || ""}
                data-testid={testid}
            />
            {helper && <p className="text-[10px] text-[var(--prc-muted)] mt-1">{helper}</p>}
        </div>
    );
}
